import { useCallback, useContext, useState, useEffect } from "react";

import castCtx from "../context/castContext";

interface Media {
  /**
   * @function playMedia - function to add an media url to chromecast to play, you should use this to first media before add more with the add function, and before use play function
   * @param src - this should be an media url acessible by chromecast
   * @param autoplay - this inidicate if media will play after defined, default is true
   */
  playMedia: (src: string, autoplay?: boolean) => Promise<any>;
  /**
   * @function addMedia - function to add an media url to chromecast queue
   * @param src - this should be an media url acessible by chromecast
   */
  addMedia: (src: string) => Promise<any>;
  /**
   * @function play - function to play media in chromecast
   */
  play: () => Promise<any>;
  /**
   * @function pause - function to pause media in chromecast
   */
  pause: () => Promise<any>;
  /**
   * this inidicate if is a media connected to chromecast
   */
  isMedia: boolean;
  /**
   * @function next - function to jump to next video in chromecast queue
   */
  next: () => Promise<any>;
  /**
   * @function prev - function to jump to prev video in chromecast queue
   */
  prev: () => Promise<any>;
  /**
   * @function to - function to jump to the time passed in seconds in chromecast playing video
   * @param seconds - time in seconds to jump to
   */
  to: (seconds: number) => Promise<any>;
}

/** @enum {string} Constants of states for media for both local and remote playback */
const PLAYER_STATE = {
  // No media is loaded into the player. For remote playback, maps to
  // the PlayerState.IDLE state.
  IDLE: 'IDLE',
  // Player is in PLAY mode but not actively playing content. For remote
  // playback, maps to the PlayerState.BUFFERING state.
  BUFFERING: 'BUFFERING',
  // The media is loaded but not playing.
  LOADED: 'LOADED',
  // The media is playing. For remote playback, maps to the PlayerState.PLAYING state.
  PLAYING: 'PLAYING',
  // The media is paused. For remote playback, maps to the PlayerState.PAUSED state.
  PAUSED: 'PAUSED'
};
let cast_player_api_available = false;
window['__onGCastApiAvailable'] = function (isAvailable) {
  if (isAvailable) {
    cast_player_api_available = true;

  }
  console.log(`cast_player_api_available: ${cast_player_api_available}`)
};

function useMedia() {
  const { session, castReceiver, castSender } = useContext(castCtx);
  const [media, setMedia] = useState<any>(null);
  const [isMedia, setIsMedia] = useState(false);
  const [remotePlayer, setRemotePlayer] = useState(null);
  const [remotePlayerController, setRemotePlayerController] = useState(null);
  const [isLiveContent, setIsLiveContent] = useState(false);
  const [mediaInfo, setMediaInfo] = useState(null);
  const [paused, setPaused] = useState(null);
  const [volume, setVolume] = useState(null);
  const [enableProgressBar, setEnableProgressBar] = useState(null);
  useEffect(() => {
    if (castSender && !remotePlayer) {
      var options: any = {};

      // Set the receiver application ID to your own (created in the
      // Google Cast Developer Console), or optionally
      // use the chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID
      options.receiverApplicationId = castReceiver.media.DEFAULT_MEDIA_RECEIVER_APP_ID;

      // Auto join policy can be one of the following three:
      // ORIGIN_SCOPED - Auto connect from same appId and page origin
      // TAB_AND_ORIGIN_SCOPED - Auto connect from same appId, page origin, and tab
      // PAGE_SCOPED - No auto connect
      options.autoJoinPolicy = castReceiver.AutoJoinPolicy.ORIGIN_SCOPED;

      /** The following flag enables Cast Connect(requires Chrome 87 or higher) */
      options.androidReceiverCompatible = true;

      castSender.CastContext.getInstance().setOptions(options);

      setRemotePlayer(new castSender.RemotePlayer());
    }
  }, [castSender]);
  useEffect(() => {
    if (remotePlayer && !remotePlayerController) {
      let controller = new castSender.RemotePlayerController(remotePlayer);
      console.log(controller)
      setRemotePlayerController(controller);
    }
  }, [remotePlayer]);

  useEffect(() => {
    if (remotePlayerController) {
      console.log(`setup remotePlayerController`);
      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.IS_CONNECTED_CHANGED,
        function (e) {
          console.log(e);
        }
      );
      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.CAN_SEEK_CHANGED,
        function (event) {
          console.log(event);
          setEnableProgressBar(event.value);
        }
      );
      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
        function () {
          setVolume(remotePlayer.volumeLevel);
        }.bind(this)
      );

      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.IS_PLAYING_BREAK_CHANGED,
        function (event) {
          console.log(event);
          this.isPlayingBreak(event.value);
        }.bind(this)
      );

      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.WHEN_SKIPPABLE_CHANGED,
        function (event) {
          console.log(event);
          this.onWhenSkippableChanged(event.value);
        }.bind(this)
      );

      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.CURRENT_BREAK_CLIP_TIME_CHANGED,
        function (event) {
          console.log(event);
          this.onCurrentBreakClipTimeChanged(event.value);
        }.bind(this)
      );

      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.BREAK_CLIP_ID_CHANGED,
        function (event) {
          console.log(event);
          this.onBreakClipIdChanged(event.value);
        }.bind(this)
      );

      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.LIVE_SEEKABLE_RANGE_CHANGED,
        function (event) {
          console.log(event);
          console.log('LIVE_SEEKABLE_RANGE_CHANGED');
          this.liveSeekableRange = event.value;
        }.bind(this)
      );
      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.IS_PAUSED_CHANGED,
        function (event) {
          console.log(event);
          if (remotePlayer.isPaused) {
            this.playerHandler.pause();
          } else if (this.playerState !== PLAYER_STATE.PLAYING) {
            // If currently not playing, start to play.
            // This occurs if starting to play from local, but this check is
            // required if the state is changed remotely.
            this.playerHandler.play();
          }
        }
      );
      remotePlayerController.addEventListener(
        castSender.RemotePlayerEventType.MEDIA_INFO_CHANGED,
        function (event) {
          console.log(event);
          let session = castSender.CastContext.getInstance().getCurrentSession();
          if (!session) {
            setMediaInfo(null);
            setIsLiveContent(false);
            return;
          }

          let media = session.getMediaSession();
          if (!media) {
            setMediaInfo(null);
            setIsLiveContent(false);
            return;
          }

          let tempMediaInfo = media.media;
          setMediaInfo(media.media);
          if (tempMediaInfo) {
            setIsLiveContent((tempMediaInfo.streamType ==
              castReceiver.media.StreamType.LIVE))
          } else {
            setIsLiveContent(false)
          }


          // if (media.playerState == PLAYER_STATE.PLAYING && this.playerState !== PLAYER_STATE.PLAYING) {
          //   this.playerHandler.prepareToPlay();
          // }

          // this.removeAdMarkers();
          // this.updateAdMarkers();

          // this.playerHandler.updateDisplay();
        }
      )
    }
  }, [remotePlayerController])
  useEffect(() => {
    if (!session && isMedia) setIsMedia(false);
  }, [session, isMedia]);

  const playMedia = useCallback(
    (src: string, autoplay?: boolean, time?: number) =>
      new Promise((res, rej) => {
        if (!castReceiver || !session)
          return rej(new Error("An Error occurred"));

        // @ts-ignore
        const mediaInfo = new castReceiver.media.MediaInfo(src);
        // @ts-ignore
        const request = new castReceiver.media.LoadRequest(mediaInfo);
        if (time !== undefined && !isNaN(time)) {
          request.currentTime = time;
        }
        request.autoplay = autoplay || true;
        session.loadMedia(
          request,
          (media: any) => {
            setMedia(media);
            setIsMedia(true);
            res(media);
          },
          (err: any) => rej(err)
        );
      }),
    [castReceiver, session]
  );

  const isMediaLoaded = useCallback(() => {
    return new Promise((res, rej) => {
      if (!session) {
        res(false);
      }
      else {
        let media = session.getMediaSession();
        if (!media) {
          res(false);
        }
        else {
          if (media.playerState == PLAYER_STATE.IDLE) {
            res(false);
          }
          else {
            // No need to verify local mediaIndex content.
            res(true);
          }
        }
      }
    })
  }, [media, session])

  const addMedia = useCallback(
    async (src: string) => {
      if (!castReceiver && !media) return;
      // @ts-ignore
      const mediaInfo = new castReceiver.media.MediaInfo(src);
      // @ts-ignore
      const queueItem = new castReceiver.media.QueueItem(mediaInfo);
      await media.queueAppendItem(queueItem);
    },
    [media, castReceiver]
  );

  const play = useCallback(async () => {
    if (!media) return;
    await media.play();
  }, [media]);

  const pause = useCallback(async () => {
    if (!media) return;
    await media.pause();
  }, [media]);

  const prev = useCallback(async () => {
    if (!media) return;
    await media.queuePrev();
  }, [media]);

  const next = useCallback(async () => {
    if (!media) return;
    await media.queueNext();
  }, [media]);

  const to = useCallback(
    async (seconds: number) => {
      if (!media && !castReceiver) return;

      // @ts-ignore
      const seek = new castReceiver.media.SeekRequest();

      seek.currentTime = seconds;

      await media.seek(seek);
    },
    [media, castReceiver]
  );

  return {
    playMedia,
    addMedia,
    isMediaLoaded,
    play,
    enableProgressBar,
    volume,
    pause,
    paused,
    isMedia,
    next,
    prev,
    to,
  } as Media;
}

export default useMedia;
